import React from 'react';
import { Box, Text, Badge, Tooltip, Anchor, Group } from '@mantine/core';
import { IconUser, IconBuilding, IconSchool, IconUsers, IconExternalLink } from '@tabler/icons-react';
import { ScimReference, isScimReference, getReferenceDisplay, getReferenceValue, getReferenceType } from '../../types/ReferenceTypes';
import { scimUrlToFullInternalRoute, isScimReferenceUrl } from '../../utils/scimUrlUtils';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { EntityCategorizationService } from '../../services/EntityCategorizationService';
import { debugVerboseLog } from '../../utils/debugLog';

interface ScimReferenceDisplayProps {
    reference: ScimReference | string | null | undefined;
    variant?: 'default' | 'compact' | 'detailed';
    showType?: boolean;
    showValue?: boolean;
    showLink?: boolean;
    maxWidth?: string | number;
    onReferenceClick?: (reference: ScimReference) => void;
}

const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
        case 'employee':
        case 'person':
        case 'user':
        case 'public':
            return <IconUser size={16} />;
        case 'school':
        case 'building':
            return <IconSchool size={16} />;
        case 'group':
        case 'class':
            return <IconUsers size={16} />;
        case 'business':
        case 'organization':
            return <IconBuilding size={16} />;
        default:
            return <IconUser size={16} />;
    }
};

export const ScimReferenceDisplay: React.FC<ScimReferenceDisplayProps> = ({
    reference,
    variant = 'default',
    showType = false,
    showValue = false,
    showLink = false,
    maxWidth = '100%',
    onReferenceClick
}) => {
    const { entityTypes } = useEntityTypes();
    const [localUrl, setLocalUrl] = React.useState<string | null>(null);

    if (!reference) {
        return <Text size="sm" c="dimmed">—</Text>;
    }

    const display = getReferenceDisplay(reference);
    const value = getReferenceValue(reference);
    const type = getReferenceType(reference);
    const isReference = isScimReference(reference);

    const canNavigateInternally = React.useMemo(() => {
        if (!reference || typeof reference !== 'object') return false;
        if ((reference as any).$ref && isScimReferenceUrl((reference as any).$ref)) {
            return true;
        }
        const refAny = reference as any;
        const refId = refAny.Id || refAny.id;
        const refType = refAny.Type || refAny.type || refAny.EntityType;
        return !!(refId && refType);
    }, [reference]);

    const generateNavigationRoute = async (ref: any): Promise<string | null> => {
        if (!ref || typeof ref !== 'object') return null;

        if (ref.$ref && isScimReferenceUrl(ref.$ref)) {
            const route = scimUrlToFullInternalRoute(ref.$ref, entityTypes);
            return route;
        }

        const refId = ref.Id || ref.id;
        const refType = ref.Type || ref.type || ref.EntityType;

        if (refId && refType) {
            const entityType = refType.toLowerCase();

            if (entityType === 'schemaattribute') {
                return `#meta/schemaattribute/${refId}`;
            } else if (entityType === 'schemaentity') {
                return `#meta/schemaentity/${refId}`;
            } else if (entityType === 'schemabinding') {
                return `#meta/schemabinding/${refId}`;
            }

            if (entityType === 'group') {
                return `#groups/view/${refId}`;
            } else if (entityType === 'class') {
                return `#classes/view/${refId}`;
            }

            try {
                const categorizationService = EntityCategorizationService.getInstance();
                const category = await categorizationService.getEntityCategory(entityType);

                let route: string;
                if (category.type === 'person') {
                    route = `#Person/${entityType}/view/${refId}`;
                } else if (category.type === 'locality') {
                    route = `#localities/${entityType}/view/${refId}`;
                } else if (category.type === 'admin') {
                    route = `#admin/${entityType}/view/${refId}`;
                } else {
                    route = `#other/${entityType}/view/${refId}`;
                }

                debugVerboseLog(`🔗 ScimReferenceDisplay: Generated route for ${entityType} (category: ${category.type}): ${route}`);
                return route;
            } catch (error) {
                console.error(`❌ ScimReferenceDisplay: Error categorizing entity type ${entityType}:`, error);
                const normalizedType = entityType.toLowerCase();
                if (
                    normalizedType === 'set' ||
                    normalizedType === 'policyrule' ||
                    normalizedType === 'journalentry' ||
                    normalizedType === 'modificationerror' ||
                    normalizedType === 'functioncatalog'
                ) {
                    const path = normalizedType === 'functioncatalog' ? 'functionCatalog' : refType;
                    return `#admin/${path}/view/${refId}`;
                }
                return `#other/${refType}/view/${refId}`;
            }
        }

        return null;
    };

    React.useEffect(() => {
        if (!reference || typeof reference !== 'object') {
            setLocalUrl(null);
            return;
        }
        setLocalUrl(null);
        let cancelled = false;
        generateNavigationRoute(reference).then((route) => {
            if (!cancelled) setLocalUrl(route);
        });
        return () => { cancelled = true; };
    }, [reference, entityTypes]);

    const fullHref = (localUrl && typeof window !== 'undefined')
        ? `${window.location.origin}${window.location.pathname}${window.location.search || ''}${localUrl}`
        : undefined;

    const tooltipContent = (localUrl || value) ? (
        <Box>
            {localUrl && (
                <Text size="xs" style={{ wordBreak: 'break-all' }}>
                    <strong>URL:</strong> {localUrl}
                </Text>
            )}
            {value && (
                <Text size="xs" mt={localUrl ? 4 : 0}>
                    <strong>ID:</strong> {value}
                </Text>
            )}
        </Box>
    ) : undefined;

    const handleInternalNavigation = async (e?: React.MouseEvent) => {
        const route = await generateNavigationRoute(reference);
        if (!route) return;

        if (e && e.button === 1) {
            e.preventDefault();
            e.stopPropagation();
            const fullUrl = window.location.origin + window.location.pathname + (window.location.search || '') + route;
            window.open(fullUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        if (onReferenceClick && reference && typeof reference === 'object') {
            e?.preventDefault();
            e?.stopPropagation();
            onReferenceClick(reference);
            return;
        }

        // Use hash assignment to push a real history entry so Back returns to the previous entity
        window.location.hash = route.startsWith('#') ? route : `#${route}`;
    };

    const renderLink = (displayText: string, styleOverrides?: React.CSSProperties) => {
        if (canNavigateInternally) {
            return (
                <Anchor
                    href={fullHref ?? '#'}
                    size="sm"
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        ...styleOverrides
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        handleInternalNavigation(e as any);
                    }}
                    underline="hover"
                >
                    {displayText}
                </Anchor>
            );
        }
        return (
            <Text size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...styleOverrides }}>
                {displayText}
            </Text>
        );
    };

    if (variant === 'compact') {
        return (
            <Group gap={4} align="center" maw={maxWidth} wrap="nowrap">
                {isReference && showType && (
                    <Tooltip label={type}>
                        <Group align="center">
                            {getTypeIcon(type)}
                        </Group>
                    </Tooltip>
                )}
                {tooltipContent ? (
                    <Tooltip label={tooltipContent}>
                        {renderLink(display, { maxWidth: '100%' })}
                    </Tooltip>
                ) : (
                    renderLink(display, { maxWidth: '100%' })
                )}
            </Group>
        );
    }

    if (variant === 'detailed') {
        return (
            <Box maw={maxWidth}>
                <Group gap="xs" mb={4}>
                    {isReference && (
                        <Badge
                            variant="outline"
                            size="sm"
                            leftSection={getTypeIcon(type)}
                        >
                            {type}
                        </Badge>
                    )}
                    {isReference && showValue && (
                        <Text size="xs" c="dimmed">
                            ID: {value}
                        </Text>
                    )}
                </Group>
                {tooltipContent ? (
                    <Tooltip label={tooltipContent}>
                        {renderLink(display, { fontWeight: 500 })}
                    </Tooltip>
                ) : (
                    renderLink(display, { fontWeight: 500 })
                )}
                {isReference && showLink && (
                    <Box mt={4}>
                        {canNavigateInternally ? (
                            <Anchor
                                href={fullHref ?? '#'}
                                size="xs"
                                onClick={(e) => { e.preventDefault(); handleInternalNavigation(e as any); }}
                                underline="hover"
                            >
                                <Group gap={4} align="center">
                                    <IconExternalLink size={14} />
                                    View Details
                                </Group>
                            </Anchor>
                        ) : (
                            <Anchor
                                href={(reference as any)?.$ref}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="xs"
                            >
                                View Details
                            </Anchor>
                        )}
                    </Box>
                )}
            </Box>
        );
    }

    // Default variant
    return (
        <Group gap="xs" align="center" maw={maxWidth} wrap="nowrap">
            {isReference && showType && (
                <Tooltip label={type}>
                    <Group align="center">
                        {getTypeIcon(type)}
                    </Group>
                </Tooltip>
            )}
            {tooltipContent ? (
                <Tooltip label={tooltipContent}>
                    {renderLink(display, { flex: 1 })}
                </Tooltip>
            ) : (
                renderLink(display, { flex: 1 })
            )}
            {isReference && showValue && (
                <Text size="xs" c="dimmed">
                    ({value})
                </Text>
            )}
        </Group>
    );
};
